// src/contexts/DemoModeContext.tsx
import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";

interface DemoModeContextType {
  demoMode: boolean;
  toggleDemoMode: () => void;
  displayName: (id?: string | number | null, realName?: string | null) => string;
}

const DemoModeContext = createContext<DemoModeContextType | null>(null);

function pseudonymFor(id: string | number): string {
  const s = String(id ?? "");
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const token = (h >>> 0).toString(36).toUpperCase().slice(0, 4).padStart(4, "0");
  return `R-${token}`;
}

export function DemoModeProvider({ children }: { children: ReactNode }) {
  const [demoMode, setDemoMode] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem("demoMode") === "1";
    } catch {
      return false;
    }
  });

  const toggleDemoMode = useCallback(() => {
    setDemoMode((prev) => {
      const next = !prev;
      try {
        sessionStorage.setItem("demoMode", next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const displayName = useCallback(
    (id?: string | number | null, realName?: string | null): string => {
      if (!demoMode) return realName ?? "";
      return pseudonymFor(id ?? realName ?? "");
    },
    [demoMode]
  );

  return (
    <DemoModeContext.Provider value={{ demoMode, toggleDemoMode, displayName }}>
      {children}
    </DemoModeContext.Provider>
  );
}

export function useDemoMode(): DemoModeContextType {
  const ctx = useContext(DemoModeContext);
  if (!ctx) throw new Error("useDemoMode must be used within a DemoModeProvider");
  return ctx;
}